import React from 'react';
import { select } from 'd3-selection';
import { scaleOrdinal } from 'd3-scale';
import { arc, pie } from 'd3-shape';
import { ascending } from 'd3-array';

import { IContributionForProductChart } from '../../../interfaces/charts.interface';
import { NumberWithCommas } from '../../../helpers/Helpers';

interface Props {
  dataSource: IContributionForProductChart[];
  component: string;
}

interface State {
  dataSource: IContributionForProductChart[];
  chartData: IContributionForProductChart[];
}

export default class PieChart extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      dataSource: this.props.dataSource,
      chartData: this.props.dataSource
    };
  }

  componentDidMount(): void {
    this.draw();
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
    if (this.props.dataSource !== prevProps.dataSource) {
      this.setState({
        chartData: this.props.dataSource
      }, () => {
        select(`.${this.props.component}`).selectAll('*').remove();
        this.draw();
      });
    }
  }

  draw(): void {
    if (this.state.dataSource.length === 0) {
      return;
    }

    // set the dimensions and margins of the graph
    const width = 1000,
      height = 450,
      margin = 40;

    // The radius of the pie plot is half the width or half the height.
    const radius: number = Math.min(width, height) / 2 - margin;

    // append the svg object to the body of the page
    const svg = select(`.${this.props.component}`)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', 'translate(' + width / 2 + ',' + height / 2 + ')');

    // const chartData = {a: 9, b: 20, c: 30, d: 8, e: 12};
    const chartData = this.state.chartData
      .reduce((acc, item) => Object.assign(acc, {[item.name]: item.value}), {});

    // List of subgroups
    const groups: string[] = this.state.chartData.map((data) => data.name);

    // List of colors
    const colors: string[] = this.state.chartData.map((data) => data.color || '');

    // set the color scale
    const color: any = scaleOrdinal()
      .domain(groups)
      .range(colors);

    // create a tooltip
    const tooltip = select(`.${this.props.component}`)
      .append('div')
      .style('opacity', 0)
      .attr('class', 'tooltip');

    // Three function that change the tooltip when user hover / move / leave a cell
    const mouseover = () => {
      tooltip
        .style('opacity', 1);
    };
    const mousemove = (event, d) => {
      tooltip
        .html(d.data[0] + '<br/>' + 'Value: ' + NumberWithCommas(d.value))
        .style('left', `${event.layerX + 10}px`)
        .style('top', `${event.layerY}px`);
    };
    const mouseleave = () => {
      tooltip
        .style('opacity', 0);
    };

    // Compute the position of each group on the pie:
    const pieChart: any = pie()
      .value((d) => d[1])
      .sort((a: any, b: any) => ascending(a.key, b.key));
    let data = pieChart(Object.entries(chartData));
    data.forEach((list1, idx: number) => {
      data[idx].pctValue = this.state.chartData[idx].pctValue;
    });

    // map to data
    const chart = svg
      .selectAll('path')
      .data(data);

    const labelArc: any = arc()
      .innerRadius(0)
      .outerRadius(radius);

    // Build the pie chart:
    chart
      .join('path')
      .attr('d', labelArc)
      .attr('fill', (d: any) => color(d.data[0]))
      .attr('stroke', 'white')
      .on('mouseover', mouseover)
      .on('mousemove', mousemove)
      .on('mouseleave', mouseleave)
      .style('stroke-width', '2px')
      .style('opacity', 1);

    const outerArc = arc()
      .innerRadius(radius * 0.9)
      .outerRadius(radius * 0.9);

    const midAngle = (d) => (d.startAngle + (d.endAngle - d.startAngle) / 2);
    svg
      .selectAll('polyline')
      .data(data)
      .enter()
      .append('polyline')
      .attr('points', (d: any) => {
        const pos = outerArc.centroid(d);
        pos[0] = radius * 0.95 * (midAngle(d) < Math.PI ? 1 : -1);

        return [labelArc.centroid(d), outerArc.centroid(d), pos];
      })
      .style('fill', 'none')
      .style('stroke', '#aaaeb5')
      .style('stroke-width', '2px');

    svg
      .selectAll('text')
      .data(data)
      .enter()
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('transform', (d: any) => {
        const pos = outerArc.centroid(d);
        pos[0] = (radius * (midAngle(d) < Math.PI ? 1 : -1)) + ((midAngle(d) < Math.PI ? 1 : -1) * 35);
        return 'translate(' + pos + ')';
      })
      .text((d: any) => `${d.value} (${d.pctValue}%)`)
      .style('font-weight', 600);
  }

  render(): React.ReactNode {
    return (
      <div
        className={`${this.props.component}`}
        style={{overflow: 'hidden'}}
      />
    )
  }

}
